import {
  useLayoutEffect, useRef, useState, type ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import {
  useLocation, useNavigationType, UNSAFE_LocationContext as LocationContext,
} from 'react-router-dom';

/**
 * SOFT EDITORIAL HANDOFF — location-driven native View Transition.
 *
 * The app renders a "displayed" location that lags the real router location.
 * Whenever the real location changes to a different pathname — no matter who
 * triggered it (link click, editor/preview navigation, programmatic navigate,
 * browser back/forward) — we start document.startViewTransition(). The browser
 * snapshots the still-unchanged old page, then the update callback commits the
 * destination synchronously via flushSync, and the browser dissolves old → new.
 * Same-path changes (filters, #anchors), unsupported browsers and reduced
 * motion commit instantly. Nothing is ever made transparent by us.
 */

type ViewTransitionLike = { finished: Promise<void>; skipTransition: () => void };
type DocWithVT = Document & {
  startViewTransition?: (cb: () => void) => ViewTransitionLike;
};

export default function RouteTransition({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [display, setDisplay] = useState({ location, navigationType });
  const ref = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  const active = useRef<ViewTransitionLike | null>(null);

  useLayoutEffect(() => {
    if (location.key === display.location.key) return;
    const next = { location, navigationType };
    const doc = document as DocWithVT;
    const samePage = location.pathname === display.location.pathname;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (active.current) {
      active.current.skipTransition();
      active.current = null;
    }

    if (samePage || reduced || !doc.startViewTransition) {
      setDisplay(next);
      return;
    }

    const vt = doc.startViewTransition(() => {
      flushSync(() => setDisplay(next));
    });
    active.current = vt;
    vt.finished.catch(() => {}).finally(() => {
      if (active.current === vt) active.current = null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Keyboard focus lands on the new page content (no scroll jump).
  useLayoutEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const main = ref.current?.querySelector('main');
    if (!main) return;
    if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
    main.focus({ preventScroll: true });
  }, [display.location.pathname]);

  return (
    <LocationContext.Provider value={display}>
      <div ref={ref}>{children}</div>
    </LocationContext.Provider>
  );
}